import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Divider,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { FileText } from 'lucide-react';
import { formatDateBr } from '../../../utils/formatters/dateUtils';
import { useReportConfig } from '../../../contexts/ReportConfigContext.jsx';
import ReportStyledField from '../ReportStyledField.jsx';

const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatGeneric = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return String(value);
};

const formatDocument = (value) => {
  if (!value) return '-';
  const digits = String(value).replace(/\D/g, '');
  if (!digits) return String(value);
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  return String(value);
};

const formatCurrencyBr = (value) => {
  const n = safeNumber(value);
  if (n === null) return '-';
  return n.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatBooleanSimNao = (value) => {
  if (value === true) return 'Sim';
  if (value === false) return 'Não';
  return '-';
};

const getProcessStatusChipColor = (statusInferido) => {
  if (!statusInferido) return 'default';
  const normalized = String(statusInferido).toLowerCase();
  if (normalized.includes('ativo')) return 'success';
  return 'default';
};

const normalizeCivilData = (raw) => {
  if (!raw || typeof raw !== 'object') return { civilstatus: null, processos: [] };

  const civilstatus =
    raw.civilstatus && typeof raw.civilstatus === 'object' ? raw.civilstatus : null;

  const processos = Array.isArray(civilstatus?.processos)
    ? civilstatus.processos.filter((p) => p && typeof p === 'object')
    : [];

  return { civilstatus, processos };
};

export default function LegalCivilDomain({ data }) {
  const { isDomainEnabled, isSectionEnabled, isFieldEnabled, getFieldLabel } = useReportConfig();

  const { civilstatus, processos } = useMemo(() => normalizeCivilData(data), [data]);
  const [statusFilter, setStatusFilter] = useState('ALL');

  if (!isDomainEnabled('legal_civil')) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Jurídico - Cível
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Domínio Jurídico - Cível desabilitado na configuração do relatório.
        </Typography>
      </Box>
    );
  }

  if (!civilstatus && !processos.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Jurídico - Cível
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nenhuma informação jurídica cível encontrada.
        </Typography>
      </Box>
    );
  }

  const nomePessoa = civilstatus?.nome;
  const identificacaoValor = civilstatus?.identificacao?.valor;
  const identificacaoTipo = civilstatus?.identificacao?.tipo;

  const totalProcessos = processos.length;
  const valorCausaAtivos = processos.reduce((acc, p) => {
    const inferido = String(p.status?.inferido || '').toLowerCase();
    const tribunalStatus = String(p.status?.tribunal || '').toLowerCase();
    const combined = `${inferido} ${tribunalStatus}`;
    if (!combined.includes('ativo')) return acc;
    const v = safeNumber(p.valor_causa);
    return acc + (v === null ? 0 : v);
  }, 0);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const filteredProcessos = useMemo(
    () =>
      processos.filter((p) => {
        if (statusFilter === 'ALL') return true;

        const inferido = String(p.status?.inferido || '').toLowerCase();
        const tribunalStatus = String(p.status?.tribunal || '').toLowerCase();
        const combined = `${inferido} ${tribunalStatus}`;

        if (statusFilter === 'ATIVO') {
          return combined.includes('ativo');
        }

        if (statusFilter === 'ARQUIVADO') {
          return combined.includes('arquiv');
        }

        if (statusFilter === 'INATIVO') {
          return !combined.includes('ativo') && !combined.includes('arquiv');
        }

        return true;
      }),
    [processos, statusFilter],
  );

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <FileText size={20} style={{ marginRight: 8 }} />
        <Typography variant="h6">Jurídico - Cível</Typography>
      </Box>

      <Card
        sx={{
          mb: 2,
          borderRadius: 2,
          borderLeft: 4,
          borderLeftColor: 'error.main',
          boxShadow: 1,
        }}
      >
        <CardContent>
          {/* Resumo da pessoa e dos processos */}
          {isSectionEnabled('legal_civil', 'resumo_pessoa') && (
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  {isFieldEnabled('legal_civil', 'resumo_pessoa', 'nome') && (
                    <ReportStyledField
                      domainKey="legal_civil"
                      fieldKey="nome"
                      variant="subtitle2"
                      defaultSx={{ mb: 0.5 }}
                    >
                      {formatGeneric(nomePessoa)}
                    </ReportStyledField>
                  )}
                  <Grid container spacing={1}>
                    {isFieldEnabled('legal_civil', 'resumo_pessoa', 'documento') && (
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {getFieldLabel('legal_civil', 'documento', 'Documento')}
                        </Typography>
                        <ReportStyledField
                          domainKey="legal_civil"
                          fieldKey="documento"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatDocument(identificacaoValor)}
                        </ReportStyledField>
                      </Grid>
                    )}
                    {isFieldEnabled('legal_civil', 'resumo_pessoa', 'tipo_identificacao') && (
                      <Grid item xs={12} sm={6}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {getFieldLabel(
                            'legal_civil',
                            'tipo_identificacao',
                            'Tipo de identificação',
                          )}
                        </Typography>
                        <ReportStyledField
                          domainKey="legal_civil"
                          fieldKey="tipo_identificacao"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatGeneric(identificacaoTipo)}
                        </ReportStyledField>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: { xs: 'flex-start', md: 'flex-end' },
                      gap: 0.75,
                    }}
                  >
                    {isFieldEnabled('legal_civil', 'resumo_pessoa', 'qtd_processos') && (
                      <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {getFieldLabel(
                            'legal_civil',
                            'qtd_processos',
                            'Quantidade de processos cíveis',
                          )}
                        </Typography>
                        <ReportStyledField
                          domainKey="legal_civil"
                          fieldKey="qtd_processos"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {totalProcessos}
                        </ReportStyledField>
                      </Box>
                    )}
                    {isFieldEnabled('legal_civil', 'resumo_pessoa', 'soma_valor_causa_ativos') && (
                      <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          {getFieldLabel(
                            'legal_civil',
                            'soma_valor_causa_ativos',
                            'Somatório valor da causa (processos Ativo)',
                          )}
                        </Typography>
                        <ReportStyledField
                          domainKey="legal_civil"
                          fieldKey="soma_valor_causa_ativos"
                          variant="body2"
                          defaultSx={{ fontWeight: 500 }}
                        >
                          {formatCurrencyBr(valorCausaAtivos)}
                        </ReportStyledField>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          {/* Lista de processos cíveis */}
          {isSectionEnabled('legal_civil', 'processos') ? (
            totalProcessos === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum processo cível identificado para esta pessoa.
              </Typography>
            ) : (
              <Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mr: 1 }}>
                    Processos cíveis
                  </Typography>
                  <Chip
                    size="small"
                    label="Ativo"
                    clickable
                    color={statusFilter === 'ATIVO' ? 'primary' : 'default'}
                    onClick={() => setStatusFilter((prev) => (prev === 'ATIVO' ? 'ALL' : 'ATIVO'))}
                  />
                  <Chip
                    size="small"
                    label="Arquivado*"
                    clickable
                    color={statusFilter === 'ARQUIVADO' ? 'primary' : 'default'}
                    onClick={() =>
                      setStatusFilter((prev) => (prev === 'ARQUIVADO' ? 'ALL' : 'ARQUIVADO'))
                    }
                  />
                  <Chip
                    size="small"
                    label="Inativo"
                    clickable
                    color={statusFilter === 'INATIVO' ? 'primary' : 'default'}
                    onClick={() =>
                      setStatusFilter((prev) => (prev === 'INATIVO' ? 'ALL' : 'INATIVO'))
                    }
                  />
                </Box>

                {filteredProcessos.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum processo correspondente ao filtro selecionado.
                  </Typography>
                ) : (
                  <Box>
                    {filteredProcessos.map((processo, index) => {
                      const numeroProcesso = processo.numero_processo;
                      const tribunal = processo.tribunal;
                      const comarca = processo.comarca;
                      const assunto = processo.assunto;
                      const natureza = processo.natureza;
                      const classeProcessual = processo.classe_processual;
                      const valorCausa = processo.valor_causa;
                      const processoPrincipal = processo.processo_principal;
                      const poloPassivo = processo.polo_passivo;
                      const papel = processo.papel;
                      const anoProcesso = processo.ano_do_processo;
                      const anoDistribuicao = processo.ano_distribuicao;
                      const forum = processo.forum;
                      const nomeNaCapa = processo.nome_na_capa;
                      const link = processo.link;

                      const statusInferido = processo.status?.inferido;
                      const statusTribunal = processo.status?.tribunal;
                      const statusData = processo.status?.data;

                      const partes = Array.isArray(processo.partes)
                        ? processo.partes.filter((p) => p && typeof p === 'object')
                        : [];
                      const advogados = Array.isArray(processo.advogados)
                        ? processo.advogados.filter((a) => a && typeof a === 'object')
                        : [];
                      const processosRelacionados = Array.isArray(processo.processos_relacionados)
                        ? processo.processos_relacionados.filter(
                            (pr) => pr && typeof pr === 'object',
                          )
                        : [];

                      const tipificacao = Array.isArray(processo.tipificacao)
                        ? processo.tipificacao
                        : [];

                      return (
                        <Accordion
                          key={numeroProcesso || index}
                          disableGutters
                          sx={{
                            mb: 1,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            '&::before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: 1,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, wordBreak: 'break-all' }}
                                >
                                  {numeroProcesso || 'Sem número de processo'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block' }}
                                >
                                  {[
                                    formatGeneric(tribunal),
                                    formatGeneric(comarca),
                                    formatGeneric(assunto),
                                  ]
                                    .filter((item) => item && item !== '-')
                                    .join(' • ') || '---'}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 0.5,
                                  alignItems: 'center',
                                }}
                              >
                                {statusInferido && (
                                  <Chip
                                    size="small"
                                    color={getProcessStatusChipColor(statusInferido)}
                                    label={statusInferido}
                                  />
                                )}
                              </Box>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Grid container spacing={2}>
                              {/* Informações principais */}
                              {isSectionEnabled('legal_civil', 'processo_info_principal') && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Informações principais
                                  </Typography>
                                  <Grid container spacing={1.5}>
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'natureza',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel('legal_civil', 'natureza', 'Natureza')}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="natureza"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatGeneric(natureza)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'classe_processual',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'classe_processual',
                                            'Classe processual',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="classe_processual"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatGeneric(classeProcessual)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'assunto',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel('legal_civil', 'assunto', 'Assunto')}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="assunto"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatGeneric(assunto)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'papel',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel('legal_civil', 'papel', 'Papel da pessoa')}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="papel"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatGeneric(papel)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'processo_principal',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'processo_principal',
                                            'Processo principal',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="processo_principal"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatBooleanSimNao(processoPrincipal)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'polo_passivo',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'polo_passivo',
                                            'Polo passivo',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="polo_passivo"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatBooleanSimNao(poloPassivo)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'ano_processo',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'ano_processo',
                                            'Ano do processo / distribuição',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="ano_processo"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {[
                                            formatGeneric(anoProcesso),
                                            formatGeneric(anoDistribuicao),
                                          ]
                                            .filter((item) => item && item !== '-')
                                            .join(' / ') || '-'}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'valor_causa',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'valor_causa',
                                            'Valor da causa',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="valor_causa"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatCurrencyBr(valorCausa)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'tribunal_uf_comarca',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'tribunal_uf_comarca',
                                            'Tribunal / UF / Comarca',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="tribunal_uf_comarca"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {[
                                            formatGeneric(tribunal),
                                            formatGeneric(processo.UF),
                                            formatGeneric(comarca),
                                          ]
                                            .filter((item) => item && item !== '-')
                                            .join(' / ') || '-'}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'forum',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel('legal_civil', 'forum', 'Fórum')}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="forum"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatGeneric(forum)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'nome_na_capa',
                                    ) && (
                                      <Grid item xs={12}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'nome_na_capa',
                                            'Nome na capa',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="nome_na_capa"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatGeneric(nomeNaCapa)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {tipificacao.length > 0 &&
                                      isFieldEnabled(
                                        'legal_civil',
                                        'processo_info_principal',
                                        'tipificacao',
                                      ) && (
                                        <Grid item xs={12}>
                                          <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: 'block' }}
                                          >
                                            {getFieldLabel(
                                              'legal_civil',
                                              'tipificacao',
                                              'Tipificação',
                                            )}
                                          </Typography>
                                          <ReportStyledField
                                            domainKey="legal_civil"
                                            fieldKey="tipificacao"
                                            variant="body2"
                                            defaultSx={{ fontWeight: 500 }}
                                          >
                                            {tipificacao.join(', ')}
                                          </ReportStyledField>
                                        </Grid>
                                      )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_info_principal',
                                      'numero_processo',
                                    ) && (
                                      <Grid item xs={12}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'numero_processo',
                                            'Número do processo',
                                          )}
                                        </Typography>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          <ReportStyledField
                                            domainKey="legal_civil"
                                            fieldKey="numero_processo"
                                            variant="body2"
                                            defaultSx={{
                                              fontWeight: 500,
                                              wordBreak: 'break-all',
                                            }}
                                          >
                                            {formatGeneric(numeroProcesso)}
                                          </ReportStyledField>
                                          {numeroProcesso && (
                                            <IconButton
                                              size="small"
                                              aria-label="Copiar número do processo"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard
                                                  .writeText(String(numeroProcesso))
                                                  .catch(() => {});
                                              }}
                                            >
                                              <ContentCopyIcon fontSize="inherit" />
                                            </IconButton>
                                          )}
                                        </Box>
                                        {link &&
                                          isFieldEnabled(
                                            'legal_civil',
                                            'processo_info_principal',
                                            'link_detalhes',
                                          ) && (
                                            <ReportStyledField
                                              domainKey="legal_civil"
                                              fieldKey="link_detalhes"
                                              variant="caption"
                                              defaultSx={{ display: 'block', mt: 0.5 }}
                                              component="a"
                                              href={link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              color="primary"
                                            >
                                              Detalhes do Processo
                                            </ReportStyledField>
                                          )}
                                      </Grid>
                                    )}
                                  </Grid>
                                </Grid>
                              )}
                              {/* Status no tribunal e datas */}
                              {isSectionEnabled('legal_civil', 'processo_status_datas') && (
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Status e datas
                                  </Typography>
                                  <Grid container spacing={1.5}>
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_status_datas',
                                      'status_inferido',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'status_inferido',
                                            'Status inferido',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="status_inferido"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatGeneric(statusInferido)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_status_datas',
                                      'status_tribunal',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'status_tribunal',
                                            'Status no tribunal',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="status_tribunal"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {formatGeneric(statusTribunal)}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_status_datas',
                                      'data_status',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'data_status',
                                            'Data de status',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="data_status"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {statusData ? formatDateBr(statusData) : '-'}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_status_datas',
                                      'data_ultima_atualizacao',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'data_ultima_atualizacao',
                                            'Última atualização',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="data_ultima_atualizacao"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {processo.data_ultima_atualizacao
                                            ? formatDateBr(processo.data_ultima_atualizacao)
                                            : '-'}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                    {isFieldEnabled(
                                      'legal_civil',
                                      'processo_status_datas',
                                      'data_andamento_mais_recente',
                                    ) && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ display: 'block' }}
                                        >
                                          {getFieldLabel(
                                            'legal_civil',
                                            'data_andamento_mais_recente',
                                            'Andamento mais recente',
                                          )}
                                        </Typography>
                                        <ReportStyledField
                                          domainKey="legal_civil"
                                          fieldKey="data_andamento_mais_recente"
                                          variant="body2"
                                          defaultSx={{ fontWeight: 500 }}
                                        >
                                          {processo.data_andamento_mais_recente
                                            ? formatDateBr(processo.data_andamento_mais_recente)
                                            : '-'}
                                        </ReportStyledField>
                                      </Grid>
                                    )}
                                  </Grid>

                                  {/* Partes */}
                                  {isSectionEnabled('legal_civil', 'processo_partes') && (
                                    <Accordion
                                      disableGutters
                                      sx={{
                                        mt: 2,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        '&::before': { display: 'none' },
                                      }}
                                    >
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2">Partes</Typography>
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        {partes.length === 0 ? (
                                          <Typography variant="body2" color="text.secondary">
                                            Nenhuma parte identificada.
                                          </Typography>
                                        ) : (
                                          <Grid container spacing={0.75}>
                                            {partes.map((parte, idx) => (
                                              <Grid item xs={12} sm={6} key={idx}>
                                                {isFieldEnabled(
                                                  'legal_civil',
                                                  'processo_partes',
                                                  'parte_papel',
                                                ) && (
                                                  <ReportStyledField
                                                    domainKey="legal_civil"
                                                    fieldKey="parte_papel"
                                                    variant="caption"
                                                    defaultSx={{
                                                      display: 'block',
                                                      color: 'text.secondary',
                                                    }}
                                                  >
                                                    {formatGeneric(parte.papel)}
                                                  </ReportStyledField>
                                                )}
                                                {isFieldEnabled(
                                                  'legal_civil',
                                                  'processo_partes',
                                                  'parte_nome',
                                                ) && (
                                                  <ReportStyledField
                                                    domainKey="legal_civil"
                                                    fieldKey="parte_nome"
                                                    variant="body2"
                                                    defaultSx={{ fontWeight: 500 }}
                                                  >
                                                    {formatGeneric(parte.nome)}
                                                  </ReportStyledField>
                                                )}
                                              </Grid>
                                            ))}
                                          </Grid>
                                        )}
                                      </AccordionDetails>
                                    </Accordion>
                                  )}

                                  {/* Advogados */}
                                  {isSectionEnabled('legal_civil', 'processo_advogados') && (
                                    <Accordion
                                      disableGutters
                                      sx={{
                                        mt: 2,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'grey.200',
                                        '&::before': { display: 'none' },
                                      }}
                                    >
                                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle2">Advogados</Typography>
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        {advogados.length === 0 ? (
                                          <Typography variant="body2" color="text.secondary">
                                            Nenhum advogado identificado.
                                          </Typography>
                                        ) : (
                                          <Grid container spacing={0.75}>
                                            {advogados.map((adv, idx) => (
                                              <Grid item xs={12} sm={6} key={idx}>
                                                {isFieldEnabled(
                                                  'legal_civil',
                                                  'processo_advogados',
                                                  'adv_nome',
                                                ) && (
                                                  <ReportStyledField
                                                    domainKey="legal_civil"
                                                    fieldKey="adv_nome"
                                                    variant="body2"
                                                    defaultSx={{ fontWeight: 500 }}
                                                  >
                                                    {formatGeneric(adv.nome)}
                                                  </ReportStyledField>
                                                )}
                                                {isFieldEnabled(
                                                  'legal_civil',
                                                  'processo_advogados',
                                                  'adv_oab',
                                                ) && (
                                                  <ReportStyledField
                                                    domainKey="legal_civil"
                                                    fieldKey="adv_oab"
                                                    variant="caption"
                                                    defaultSx={{
                                                      display: 'block',
                                                      color: 'text.secondary',
                                                    }}
                                                  >
                                                    {adv.oab
                                                      ? `OAB: ${adv.oab}`
                                                      : 'OAB não informada'}
                                                  </ReportStyledField>
                                                )}
                                              </Grid>
                                            ))}
                                          </Grid>
                                        )}
                                      </AccordionDetails>
                                    </Accordion>
                                  )}

                                  {/* Processos relacionados */}
                                  {isSectionEnabled('legal_civil', 'processo_relacionados') &&
                                    processosRelacionados.length > 0 && (
                                      <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                          Processos relacionados
                                        </Typography>
                                        <Grid container spacing={0.75}>
                                          {processosRelacionados.map((rel, idx) => (
                                            <Grid item xs={12} key={idx}>
                                              {isFieldEnabled(
                                                'legal_civil',
                                                'processo_relacionados',
                                                'rel_numero_processo',
                                              ) && (
                                                <ReportStyledField
                                                  domainKey="legal_civil"
                                                  fieldKey="rel_numero_processo"
                                                  variant="body2"
                                                  defaultSx={{
                                                    fontWeight: 500,
                                                    wordBreak: 'break-all',
                                                  }}
                                                >
                                                  {formatGeneric(rel.numero_processo)}
                                                </ReportStyledField>
                                              )}
                                              {rel.link &&
                                                isFieldEnabled(
                                                  'legal_civil',
                                                  'processo_relacionados',
                                                  'rel_link',
                                                ) && (
                                                  <ReportStyledField
                                                    domainKey="legal_civil"
                                                    fieldKey="rel_link"
                                                    variant="caption"
                                                    defaultSx={{ display: 'block' }}
                                                    component="a"
                                                    href={rel.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    color="primary"
                                                  >
                                                    Detalhes do Processo
                                                  </ReportStyledField>
                                                )}
                                            </Grid>
                                          ))}
                                        </Grid>
                                      </Box>
                                    )}
                                </Grid>
                              )}
                            </Grid>
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Box>
                )}
              </Box>
            )
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}
